/**
 * Session Card Component
 *
 * A card-based display for session information, designed for mobile-friendly staff viewing.
 * Shows session name, status, type, date range, total classes, and registration window.
 */

import {
  Badge,
  Box,
  Card,
  Flex,
  HStack,
  Icon,
  Text,
  VStack,
} from "@chakra-ui/react"
import { FiCalendar, FiClock, FiList } from "react-icons/fi"

import { SessionActionsMenu } from "@/components/layout/Common/SessionActionsMenu"
import type { SessionResponse } from "@/types/session"

const sessionTypeLabels: Record<string, string> = {
  regular_season: "Regular Season",
  summer_camp: "Summer Camp",
  winter_intensive: "Winter Intensive",
  spring_break: "Spring Break",
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "N/A"
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString()
}

function formatDatetime(value: string | null | undefined): string {
  if (!value) return "N/A"
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString()
}

export interface SessionCardProps {
  session: SessionResponse
  /** Whether to show action menu */
  showActions?: boolean
  /** Click handler for the card */
  onClick?: () => void
}

export function SessionCard({
  session,
  showActions = true,
  onClick,
}: SessionCardProps) {
  const isActive = session.is_active !== false
  const isPublished = session.is_published !== false
  const status = !isActive ? "Inactive" : !isPublished ? "Draft" : "Published"
  const statusColor = !isActive ? "gray" : !isPublished ? "orange" : "green"

  const typeLabel = session.session_type
    ? sessionTypeLabels[session.session_type] || session.session_type
    : null

  return (
    <Card.Root
      bg="white"
      borderRadius="xl"
      boxShadow="sm"
      borderWidth="1px"
      borderColor="gray.100"
      _hover={{
        boxShadow: "md",
        borderColor: "ui.main",
        transform: onClick ? "translateY(-2px)" : undefined,
      }}
      transition="all 0.2s"
      cursor={onClick ? "pointer" : "default"}
      onClick={onClick}
    >
      <Card.Body p={5}>
        <VStack align="stretch" gap={4}>
          {/* Header with session name and status */}
          <Flex justify="space-between" align="start">
            <Box flex={1}>
              <Text fontWeight="semibold" fontSize="lg" color="gray.800">
                {session.name}
              </Text>
              <HStack gap={2} mt={1}>
                <Badge
                  colorPalette={statusColor}
                  variant="subtle"
                  size="sm"
                  borderRadius="full"
                >
                  {status}
                </Badge>
                {typeLabel && (
                  <Badge variant="outline" size="sm" borderRadius="full">
                    {typeLabel}
                  </Badge>
                )}
              </HStack>
            </Box>

            {/* Actions menu */}
            {showActions && (
              <Box onClick={(e) => e.stopPropagation()}>
                <SessionActionsMenu session={session} />
              </Box>
            )}
          </Flex>

          {/* Date range + Total classes */}
          <VStack align="stretch" gap={2}>
            <HStack fontSize="sm" color="gray.600">
              <Icon color="gray.400">
                <FiCalendar />
              </Icon>
              <Text>
                {formatDate(session.start_week)} -{" "}
                {formatDate(session.end_week)}
              </Text>
            </HStack>

            <HStack fontSize="sm" color="gray.600">
              <Icon color="gray.400">
                <FiList />
              </Icon>
              <Text>{session.total_classes} classes</Text>
            </HStack>
          </VStack>

          {/* Registration window */}
          {(session.registration_opens || session.registration_closes) && (
            <Box
              bg="blue.50"
              borderRadius="md"
              p={3}
              borderLeftWidth="3px"
              borderLeftColor="blue.400"
            >
              <HStack color="blue.600" fontSize="sm" mb={1}>
                <Icon>
                  <FiClock />
                </Icon>
                <Text fontWeight="medium">Registration Window</Text>
              </HStack>
              <Text fontSize="sm" color="gray.700">
                {formatDatetime(session.registration_opens)} -{" "}
                {formatDatetime(session.registration_closes)}
              </Text>
            </Box>
          )}
        </VStack>
      </Card.Body>
    </Card.Root>
  )
}

export default SessionCard
